import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { nomeDeExibicao } from '@/lib/usuario';
import { emApp } from '@/lib/app';
import { marcarCodigoAberto, codigoJaAberto } from '@/lib/inscricaoCodigo';
import { lerRascunhoAberto } from '@/lib/rascunhoInscricao';
import { marcarTemCompra } from '@/lib/pedidosApp';
import AppScreen from '@/components/ui/AppScreen';
import ListaEntrada from '@/components/movimento/ListaEntrada';
import EmptyState from '@/components/ui/EmptyState';
import { useMenuApp } from '@/components/feature/MenuApp';
import BotaoTenhoCodigo from '@/components/feature/TenhoCodigo';
import CartaoOrganizador from '@/pages/home/components/CartaoOrganizador';
import CartaoContinuarInscricao from '@/pages/home/components/CartaoContinuarInscricao';
import TopoInicio, { type CidadeOpcao } from '@/pages/home/components/inicio/TopoInicio';
import CartaoOracao, { CartaoSabia } from '@/pages/home/components/inicio/CartaoOracao';
import { CartaoEmBreve, CartaoVenda, RodapeInicio } from '@/pages/home/components/inicio/CartoesEvento';
import EsqueletoInicio from '@/pages/home/components/inicio/EsqueletoInicio';
import {
  type ItemVitrine,
  carregarVitrineVenda,
  chaveCidade,
  cidadePrincipal,
  entraNaFaixa,
} from '@/pages/home/vitrineVenda';
import { eventosComTaxa } from '@/lib/taxa';
import { oracaoDeHoje, minhaOracaoSituacao, type OracaoHoje } from '@/lib/oracao';
import { curiosidadeVista } from '@/lib/oracaoVista';
import {
  buscarMinhaTribo,
  buscarCuriosidadeDaMinhaTribo,
  COR_TRIBO_PADRAO,
} from '@/lib/tribo';

interface SituApp {
  evento_id: number;
  eu_estou_inscrito: boolean;
  meu_codigo: string | null;
  meu_status: string | null;
}

interface IngApp {
  evento_id: number;
  pedido_id: number;
  status_pedido: string;
}

interface Sabia {
  triboId: number;
  cor: string;
  texto: string;
  nova: boolean;
  semanal: string;
}

const CHAVE_CIDADE = 'seliga:vitrine_cidade';

function lerCidade(): string {
  try {
    return chaveCidade(localStorage.getItem(CHAVE_CIDADE) ?? '');
  } catch {
    return '';
  }
}

function guardarCidade(valor: string): void {
  try {
    if (valor) localStorage.setItem(CHAVE_CIDADE, valor);
    else localStorage.removeItem(CHAVE_CIDADE);
  } catch {
    /* ignora ambientes sem armazenamento */
  }
}

function saudacao(): string {
  let h = new Date().getHours();
  try {
    h = Number(
      new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        hour: 'numeric',
        hour12: false,
      }).format(new Date()),
    );
  } catch {
    /* usa a hora local se o fuso não estiver disponível */
  }
  if (h >= 5 && h < 12) return 'Bom dia,';
  if (h >= 12 && h < 18) return 'Boa tarde,';
  return 'Boa noite,';
}

// Um único fade de tela (150 ms): nada de animação cartão por cartão.
function Entra({ children }: { i?: number; children: ReactNode }) {
  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Início pública: a porta de entrada de quem chega pelo celular. Oração de
// hoje no topo (identidade verde/dourado), depois os eventos — cada um uma
// única vez — e um rodapé curto. Nada de busca aqui: a busca mora na Agenda.
// ---------------------------------------------------------------------------
export default function AppInicio() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { abrir: abrirMenu, logado: menuLogado, organiza } = useMenuApp();
  const noApp = emApp();
  const [nome, setNome] = useState('');
  const [itens, setItens] = useState<ItemVitrine[]>([]);
  const [situacoes, setSituacoes] = useState<Map<number, SituApp>>(new Map());
  const [ingressos, setIngressos] = useState<Map<number, IngApp>>(new Map());
  const [comTaxa, setComTaxa] = useState<Set<number>>(new Set());
  const [cidade, setCidade] = useState<string>(() => lerCidade());
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [oracao, setOracao] = useState<OracaoHoje | null>(null);
  const [oracaoCarregando, setOracaoCarregando] = useState(true);
  const [rezeiHoje, setRezeiHoje] = useState(false);
  const [sabia, setSabia] = useState<Sabia | null>(null);

  // Saudação: usa o primeiro nome do PERFIL (nunca o e-mail).
  useEffect(() => {
    let ativo = true;
    (async () => {
      if (!user) {
        if (ativo) setNome('');
        return;
      }
      const n = await nomeDeExibicao(user);
      if (ativo) setNome(n);
    })();
    return () => {
      ativo = false;
    };
  }, [user]);

  // App instalado: se existe um código de convite válido, abre o evento uma vez.
  useEffect(() => {
    if (!noApp) return;
    let ativo = true;
    void (async () => {
      const salvo = await lerRascunhoAberto();
      if (!ativo || !salvo) return;
      if (codigoJaAberto(salvo.slug, salvo.codigo)) return;
      marcarCodigoAberto(salvo.slug, salvo.codigo);
      navigate(`/e/${salvo.slug}?c=${encodeURIComponent(salvo.codigo)}`, { replace: true });
    })();
    return () => {
      ativo = false;
    };
  }, [noApp, navigate]);

  // Oração de hoje: mesma fonte da tela /oracao (nenhuma chamada nova).
  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const [o, s] = await Promise.all([oracaoDeHoje(), minhaOracaoSituacao()]);
        if (!ativo) return;
        setOracao(o);
        setRezeiHoje(s?.rezei_hoje === true);
      } finally {
        if (ativo) setOracaoCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  // "Você sabia?" — só para quem está logado e tem tribo.
  useEffect(() => {
    if (!user) {
      setSabia(null);
      return;
    }
    let ativo = true;
    (async () => {
      try {
        const minha = await buscarMinhaTribo();
        if (!ativo || !minha.tribo) return;
        const dias = await buscarCuriosidadeDaMinhaTribo();
        if (!ativo || !dias.tem || !dias.texto) return;
        setSabia({
          triboId: minha.tribo.id,
          cor: minha.tribo.cor ?? COR_TRIBO_PADRAO,
          texto: dias.texto,
          semanal: dias.semana,
          nova: dias.nova === true && !curiosidadeVista(dias.semana),
        });
      } catch {
        /* silencioso: o cartão simplesmente não aparece */
      }
    })();
    return () => {
      ativo = false;
    };
  }, [user]);

  // Vitrine (uma chamada só) + situação da pessoa + taxa.
  useEffect(() => {
    let ativo = true;

    async function carregar() {
      setCarregando(true);
      setErro(null);
      try {
        const lista = await carregarVitrineVenda();

        const [situRes, ingRes, comTaxaSet] = await Promise.all([
          supabase
            .from('vw_minha_situacao_evento')
            .select('evento_id, eu_estou_inscrito, meu_codigo, meu_status'),
          supabase.from('vw_meus_ingressos').select('evento_id, pedido_id, status_pedido'),
          eventosComTaxa(lista.map((i) => i.evento_id)),
        ]);

        const mapaSitu = new Map<number, SituApp>();
        ((situRes.data as SituApp[]) || []).forEach((s) => mapaSitu.set(s.evento_id, s));

        const mapaIng = new Map<number, IngApp>();
        ((ingRes.data as IngApp[]) || []).forEach((ing) => {
          const atual = mapaIng.get(ing.evento_id);
          if (!atual) {
            mapaIng.set(ing.evento_id, ing);
            return;
          }
          if (ing.status_pedido === 'pago' && atual.status_pedido !== 'pago') {
            mapaIng.set(ing.evento_id, ing);
            return;
          }
          if (ing.status_pedido === atual.status_pedido && ing.pedido_id > atual.pedido_id) {
            mapaIng.set(ing.evento_id, ing);
          }
        });

        if (ativo) {
          setItens(lista);
          setSituacoes(mapaSitu);
          setIngressos(mapaIng);
          setComTaxa(comTaxaSet);
        }
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar os eventos. Tente de novo.'));
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [reload]);

  const temIngresso = useMemo(
    () =>
      Array.from(ingressos.values()).some(
        (i) => i.status_pedido === 'pago' || i.status_pedido === 'aguardando',
      ),
    [ingressos],
  );

  const temInscricao = useMemo(
    () => Array.from(situacoes.values()).some((s) => s.eu_estou_inscrito),
    [situacoes],
  );

  // Avisa os pedidos de abertura: quem já comprou vê "Guarde seu ingresso".
  useEffect(() => {
    if (temIngresso || temInscricao) marcarTemCompra();
  }, [temIngresso, temInscricao]);

  // Chips: só a primeira cidade antes de "/" ou "," e sem repetir por acento.
  const cidades = useMemo<CidadeOpcao[]>(() => {
    const mapa = new Map<string, string>();
    itens.forEach((i) => {
      const chave = chaveCidade(i.cidade);
      const rotulo = cidadePrincipal(i.cidade);
      if (chave && rotulo && !mapa.has(chave)) mapa.set(chave, rotulo);
    });
    return [...mapa.entries()].map(([chave, rotulo]) => ({ chave, rotulo }));
  }, [itens]);

  const cidadeAtiva = useMemo(
    () => (cidades.some((c) => c.chave === cidade) ? cidade : ''),
    [cidades, cidade],
  );

  const daCidade = useMemo(
    () => itens.filter((i) => !cidadeAtiva || chaveCidade(i.cidade) === cidadeAtiva),
    [itens, cidadeAtiva],
  );

  // Cada evento aparece UMA vez: quem vende vai para "À venda agora",
  // o resto para "Em breve".
  const aVenda = useMemo(() => daCidade.filter(entraNaFaixa), [daCidade]);
  const emBreve = useMemo(() => daCidade.filter((i) => !entraNaFaixa(i)), [daCidade]);

  const escolherCidade = (valor: string) => {
    setCidade(valor);
    guardarCidade(valor);
  };

  return (
    <AppScreen>
      <TopoInicio
        logado={menuLogado}
        nome={nome}
        saudacao={saudacao()}
        cidades={cidades}
        cidade={cidadeAtiva}
        aoEscolherCidade={escolherCidade}
        aoAbrirMenu={abrirMenu}
      />

      <main className="app-entra mx-auto w-full max-w-[720px] overflow-x-clip px-4 pb-4">
        <Entra i={0}>
          <CartaoOracao oracao={oracao} rezeiHoje={rezeiHoje} carregando={oracaoCarregando} />
        </Entra>

        {sabia && (
          <Entra i={1}>
            <CartaoSabia
              triboId={sabia.triboId}
              cor={sabia.cor}
              texto={sabia.texto}
              nova={sabia.nova}
              semana={sabia.semanal}
            />
          </Entra>
        )}

        {noApp && <CartaoContinuarInscricao />}
        {noApp && (
          <div className="mt-2 flex justify-end">
            <BotaoTenhoCodigo />
          </div>
        )}

        {/* Seus ingressos e inscrições: acima dos eventos, sem repetir */}
        {!carregando && !erro && (temIngresso || temInscricao) && (
          <Entra i={2}>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {temIngresso && (
                <Link
                  to="/meus-ingressos"
                  className="aperta flex items-center gap-3 rounded-[16px] bg-background-50 px-4 py-3 shadow-pcard"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                    <i className="ri-ticket-2-line text-[18px]" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] font-extrabold text-foreground-950">
                      Seus ingressos e fichas
                    </span>
                    <span className="block truncate text-[12px] text-foreground-600">
                      Abra o QR na portaria
                    </span>
                  </span>
                  <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden />
                </Link>
              )}
              {temInscricao && (
                <Link
                  to="/minhas-inscricoes"
                  className="aperta flex items-center gap-3 rounded-[16px] bg-background-50 px-4 py-3 shadow-pcard"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                    <i className="ri-file-list-3-line text-[18px]" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] font-extrabold text-foreground-950">
                      Suas inscrições
                    </span>
                    <span className="block truncate text-[12px] text-foreground-600">
                      Veja a situação de cada uma
                    </span>
                  </span>
                  <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden />
                </Link>
              )}
            </div>
          </Entra>
        )}

        {carregando && <EsqueletoInicio />}

        {!carregando && erro && (
          <div className="mt-4">
            <EmptyState
              icone="ri-wifi-off-line"
              titulo="Não deu para carregar"
              descricao={erro}
              acao={
                <button
                  type="button"
                  onClick={() => setReload((r) => r + 1)}
                  className="aperta h-12 cursor-pointer whitespace-nowrap rounded-[14px] bg-primary-500 px-6 text-[15px] font-bold text-white"
                >
                  Tentar de novo
                </button>
              }
            />
          </div>
        )}

        {!carregando && !erro && cidades.length > 1 && (
          <div className="-mx-4 mt-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <button
              type="button"
              onClick={() => escolherCidade('')}
              className={`aperta flex h-8 shrink-0 cursor-pointer items-center whitespace-nowrap rounded-full px-3 text-[12.5px] font-bold ${
                cidadeAtiva === ''
                  ? 'bg-primary-500 text-white'
                  : 'border border-background-300 bg-background-50 text-foreground-700'
              }`}
            >
              Todas
            </button>
            {cidades.map((c) => (
              <button
                key={c.chave}
                type="button"
                onClick={() => escolherCidade(c.chave)}
                className={`aperta flex h-8 shrink-0 cursor-pointer items-center whitespace-nowrap rounded-full px-3 text-[12.5px] font-bold ${
                  cidadeAtiva === c.chave
                    ? 'bg-primary-500 text-white'
                    : 'border border-background-300 bg-background-50 text-foreground-700'
                }`}
              >
                {c.rotulo}
              </button>
            ))}
          </div>
        )}

        {!carregando && !erro && aVenda.length > 0 && (
          <section className="mt-5" aria-labelledby="a-venda">
            <Entra i={2}>
              <div className="flex items-end justify-between gap-3">
                <h2 id="a-venda" className="text-[18px] font-extrabold text-foreground-950">
                  À venda agora
                </h2>
                <span className="whitespace-nowrap text-[12px] font-semibold text-foreground-600">
                  {aVenda.length === 1 ? '1 evento' : `${aVenda.length} eventos`}
                </span>
              </div>
            </Entra>

            <ListaEntrada
              itens={aVenda}
              chave={(item) => item.evento_id}
              className="mt-3 space-y-3"
            >
              {(item, i) => (
                <CartaoVenda
                  item={item}
                  comTaxa={comTaxa.has(item.evento_id)}
                  prioridade={i === 0}
                />
              )}
            </ListaEntrada>
          </section>
        )}

        {!carregando && !erro && emBreve.length > 0 && (
          <section className="mt-6" aria-labelledby="em-breve">
            <Entra i={3}>
              <h2 id="em-breve" className="text-[18px] font-extrabold text-foreground-950">
                Em breve
              </h2>
            </Entra>
            <ListaEntrada
              itens={emBreve}
              chave={(item) => item.evento_id}
              className="mt-3 space-y-2.5"
            >
              {(item) => <CartaoEmBreve item={item} />}
            </ListaEntrada>
          </section>
        )}

        {!carregando && !erro && itens.length === 0 && (
          <div className="mt-4">
            <EmptyState
              icone="ri-calendar-2-line"
              titulo="Nada por aqui ainda"
              descricao="Quando rolar um evento novo, ele aparece aqui."
            />
          </div>
        )}

        {!carregando && !erro && itens.length > 0 && daCidade.length === 0 && (
          <div className="mt-4">
            <EmptyState
              icone="ri-map-pin-line"
              titulo="Nenhum evento nessa cidade"
              descricao="Toque em “Todas” para ver o que está chegando."
              acao={
                <button
                  type="button"
                  onClick={() => escolherCidade('')}
                  className="aperta h-12 cursor-pointer whitespace-nowrap rounded-[14px] bg-primary-500 px-6 text-[15px] font-bold text-white"
                >
                  Ver todas
                </button>
              }
            />
          </div>
        )}

        {!carregando && !erro && (
          <Entra i={4}>
            <div className="mt-6">
              <CartaoOrganizador organiza={organiza} />
            </div>
          </Entra>
        )}
      </main>

      <RodapeInicio />
    </AppScreen>
  );
}